import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Bell, Heart, User, Menu, Settings, LogOut, Lock, X, Palette, Check, UserPlus, Shield, Megaphone, ExternalLink, CheckCircle, XCircle, ShoppingBag, Share2, Award } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import useAdultMode from '../../hooks/useAdultMode';
import { useHaptic } from '../../hooks/useHaptic';
import { initPushNotifications, nativeShare } from '../../utils/capacitor';
import SafetyNetModal from '../SafetyNetModal';
import { SEARCH_ITEMS } from './topNav/searchItems';
import { fuzzyFilter } from './topNav/fuzzyMatch';
import { apiClient } from '../../api/client';
import { VoiceMinutesCounter } from './topNav/VoiceMinutesCounter';

const TopNav = ({ onMenuClick }) => {
  const { user, logout, lockSession } = useAuth();
  const { currentTheme, changeTheme, themes } = useTheme();
  const navigate = useNavigate();
  const haptic = useHaptic();
  const isAdultMode = useAdultMode();
  const [showNotifications, setShowNotifications] = useState(false);
  const [expandedNotifId, setExpandedNotifId] = useState(null);
  const [showSafetyNet, setShowSafetyNet] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showThemes, setShowThemes] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const searchRef = useRef(null);
  const themeRef = useRef(null);
  const notifRef = useRef(null);

  // Initialize push notifications on mount (no-op on web)
  useEffect(() => {
    initPushNotifications((notification) => {
      // When a push arrives while app is open, bump the badge
      setUnreadCount(prev => prev + 1);
      fetchNotifications();
      haptic.success();
    });
    // Verified mount-only: registers the push notification callback with
    // the native bridge exactly once per app session. Re-running this on
    // `haptic`/`fetchNotifications` reference flips would try to re-init
    // the native push channel — the Capacitor plugin doesn't gracefully
    // handle repeat registration. Both refs are used inside the callback
    // via closure; `fetchNotifications` is itself useCallback-stable and
    // `haptic` from `useHaptic()` is a stable object per hook contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const themeOrder = ['signature', 'ocean', 'sunset', 'lavender', 'forest', 'midnight', 'memorial', 'sunrise', 'minimal', 'neon'];

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      if (!token) return;
      const res = await fetch(`${API_URL}/api/notifications/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (e) { /* silent */ }
  }, [API_URL]);

  // Fetch notifications on mount and every 30s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      await fetch(`${API_URL}/api/notifications/mark-read/${notificationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const respondToNotification = async (notificationId, action) => {
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      await fetch(`${API_URL}/api/notifications/respond/${notificationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      setNotifications(prev => prev.map(n =>
        n.notification_id === notificationId ? { ...n, read: true, responded: true, response_action: action } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
      if (themeRef.current && !themeRef.current.contains(e.target)) setShowThemes(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Feb 2026 — typo-tolerant fuzzy match (Levenshtein ≤ 2). Keeps exact
  // substring hits at the top, then fills in close matches like
  // "challange" → Challenges, "leadrbord" → Leaderboard.
  const filteredResults = searchQuery.trim()
    ? fuzzyFilter(SEARCH_ITEMS, searchQuery, 2)
    : [];

  // Feb 2026 — telemetry: log dashboard searches that returned no results
  // so the Master Admin can see what teens are asking for that we don't
  // yet offer. Debounced 900 ms after the last keystroke; sends only
  // when results were truly empty AND the query is ≥ 3 chars.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3 || filteredResults.length > 0) return undefined;
    const timer = setTimeout(() => {
      apiClient
        .request('/api/search/unmatched', {
          method: 'POST',
          body: JSON.stringify({ query: q }),
        })
        .catch(() => { /* telemetry is best-effort */ });
    }, 900);
    return () => clearTimeout(timer);
  }, [searchQuery, filteredResults.length]);

  const handleResultClick = (path) => {
    navigate(path);
    setSearchQuery('');
    setShowResults(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && filteredResults.length > 0) {
      handleResultClick(filteredResults[0].path);
    }
    if (e.key === 'Escape') {
      setShowResults(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      <nav className="top-nav">
        {/* Left Section */}
        <div className="nav-left">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuClick}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <Menu size={24} className="text-gray-700" />
          </button>

          {/* Search Bar (desktop only — mobile nav is too crowded to fit
              a full search input alongside hamburger + logo + hearts) */}
          <div className="search-bar hidden md:flex relative" ref={searchRef}>
            <Search size={18} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search pages, features..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowResults(true); }}
              onFocus={() => searchQuery && setShowResults(true)}
              onKeyDown={handleSearchKeyDown}
              className="flex-1 bg-transparent border-none outline-none ring-0 focus:outline-none focus:ring-0 text-sm text-gray-700 placeholder-gray-400"
              data-testid="search-input"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setShowResults(false); }} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            )}
            {/* Search Results Dropdown */}
            {showResults && searchQuery.trim() && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50" data-testid="search-results">
                {filteredResults.length > 0 ? (
                  filteredResults.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleResultClick(item.path)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      data-testid={`search-result-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Search size={14} className="text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800">{item.label}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-4 text-sm text-gray-600" data-testid="search-no-results">
                    <div className="flex items-start gap-2 mb-3">
                      <span className="text-gray-500">No results for</span>
                      <span className="font-semibold text-gray-900">&ldquo;{searchQuery}&rdquo;</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const q = searchQuery.trim();
                        setShowResults(false);
                        setSearchQuery('');
                        window.dispatchEvent(new CustomEvent('hibff:open-idea-modal', {
                          detail: { sourceQuery: q },
                        }));
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #F5831F 0%, #DC2626 100%)' }}
                      data-testid="search-suggest-as-idea"
                    >
                      ✨ Suggest this as an idea — earn an NFT
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="nav-right">
          {/* Voice Minutes Counter — HIDDEN FOR US B2C LAUNCH (May 22, 2026).
              `voice_minutes_used` is a cosmetic field that is never
              incremented in the STT/TTS hot path, so showing "240/240 min"
              would be misleading. The component is preserved verbatim
              below — un-comment to re-enable once real metering is wired
              in `/api/tts/*` and the realtime STT path. */}
          {/* <VoiceMinutesCounter user={user} /> */}

          {/* Theme Selector Dropdown */}
          <div className="relative hidden md:block" ref={themeRef}>
            <button
              onClick={() => setShowThemes(!showThemes)}
              className="nav-icon-button"
              aria-label="Change theme"
              data-testid="theme-selector-button"
            >
              <Palette size={20} className="text-purple-500" />
            </button>
            {showThemes && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto" data-testid="theme-dropdown">
                <div className="p-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-sm">Choose Theme</h3>
                </div>
                <div className="p-1.5">
                  {themeOrder.map((name) => {
                    const theme = themes[name];
                    if (!theme) return null;
                    return (
                      <button
                        key={name}
                        onClick={() => { changeTheme(name); setShowThemes(false); }}
                        className={`w-full flex items-center justify-between p-2.5 rounded-lg mb-0.5 transition-all ${
                          currentTheme === name ? 'bg-blue-50 border border-blue-300' : 'hover:bg-gray-50 border border-transparent'
                        }`}
                        data-testid={`theme-option-${name}`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="flex gap-1">
                            <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: theme.cssVars['--sidebar-top'] }} />
                            <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: theme.cssVars['--accent-primary'] }} />
                            <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: theme.cssVars['--accent-secondary'] }} />
                          </div>
                          <span className="text-sm font-medium text-gray-800">{theme.name}</span>
                        </div>
                        {currentTheme === name && <Check size={16} className="text-blue-600" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => { setShowNotifications(!showNotifications); haptic.light(); }}
              className="nav-icon-button relative"
              aria-label="Notifications"
              data-testid="notifications-button"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown — Smart Notification Center */}
            {showNotifications && (
              <div
                className="absolute right-1 sm:right-0 mt-2 w-[calc(100vw-1rem)] sm:w-96 max-w-[26rem] bg-white rounded-xl shadow-xl border border-gray-200 z-50 flex flex-col"
                style={{ maxHeight: 'min(75vh, 560px)' }}
                data-testid="notifications-dropdown"
              >
                <div className="p-3 border-b border-gray-200 flex items-center justify-between shrink-0">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="mark-all-read">
                        Mark all read
                      </button>
                    )}
                  </div>
                </div>

                {/* Grouped notifications */}
                <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500">No notifications yet</div>
                  ) : (() => {
                    // Group notifications by type
                    const groups = {};
                    notifications.forEach(n => {
                      const gKey = n.type === 'admin' ? 'Platform' : n.type === 'quest' ? 'Quests' : n.type === 'social' ? 'Social' : n.type === 'family' ? 'Family' : n.type === 'nft' ? 'NFT' : 'Other';
                      if (!groups[gKey]) groups[gKey] = [];
                      groups[gKey].push(n);
                    });

                    const groupOrder = ['Platform', 'Quests', 'Social', 'Family', 'NFT', 'Other'];
                    const groupIcons = {
                      Platform: Megaphone,
                      Quests: Shield,
                      Social: UserPlus,
                      Family: UserPlus,
                      NFT: ExternalLink,
                      Other: Bell,
                    };
                    const groupColors = {
                      Platform: 'text-orange-600 bg-orange-50',
                      Quests: 'text-violet-600 bg-violet-50',
                      Social: 'text-blue-600 bg-blue-50',
                      Family: 'text-green-600 bg-green-50',
                      NFT: 'text-purple-600 bg-purple-50',
                      Other: 'text-gray-600 bg-gray-50',
                    };

                    return groupOrder.filter(g => groups[g]).map(groupName => {
                      const items = groups[groupName];
                      const GIcon = groupIcons[groupName] || Bell;
                      const colors = groupColors[groupName] || 'text-gray-600 bg-gray-50';
                      const unreadInGroup = items.filter(n => !n.read).length;

                      return (
                        <div key={groupName}>
                          <div className="px-3 pt-2.5 pb-1 flex items-center gap-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider ${colors.split(' ')[0]}`}>{groupName}</span>
                            {unreadInGroup > 0 && (
                              <span className="text-[10px] font-bold text-white bg-red-500 rounded-full w-4 h-4 flex items-center justify-center">{unreadInGroup}</span>
                            )}
                          </div>
                          {items.map(notif => {
                            const bgColor = notif.read ? 'bg-white' : colors.split(' ')[1] || 'bg-blue-50';
                            const isExpanded = expandedNotifId === notif.notification_id;
                            const isLong = (notif.message || '').length > 90;
                            return (
                              <div
                                key={notif.notification_id}
                                className={`px-3 py-2.5 border-b border-gray-50 last:border-b-0 ${bgColor} hover:bg-gray-50 transition-colors cursor-pointer`}
                                onClick={() => {
                                  if (!notif.read) markAsRead(notif.notification_id);
                                  setExpandedNotifId(prev => prev === notif.notification_id ? null : notif.notification_id);
                                }}
                                data-testid={`notification-${notif.notification_id}`}
                              >
                                <div className="flex gap-2.5">
                                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${colors}`}>
                                    <GIcon size={12} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <p className={`text-sm font-semibold text-gray-900 ${isExpanded ? '' : 'truncate'}`}>{notif.title}</p>
                                      {!notif.read && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />}
                                    </div>
                                    <p className={`text-xs text-gray-500 mt-0.5 whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-2'}`}>{notif.message}</p>
                                    {isLong && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); if (!notif.read) markAsRead(notif.notification_id); setExpandedNotifId(prev => prev === notif.notification_id ? null : notif.notification_id); }}
                                        className="mt-0.5 text-[11px] font-semibold text-blue-600 hover:text-blue-800"
                                        data-testid={`notif-expand-${notif.notification_id}`}
                                      >
                                        {isExpanded ? 'Show less' : 'Read more'}
                                      </button>
                                    )}

                                    {/* Contextual action */}
                                    {notif.link && !notif.actionable && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); navigate(notif.link); setShowNotifications(false); }}
                                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1 transition-colors"
                                        style={{ backgroundColor: '#FFF3EB', color: '#F5831F' }}
                                        data-testid={`notif-link-${notif.notification_id}`}
                                      >
                                        {notif.link_label || 'View'} <ExternalLink size={10} />
                                      </button>
                                    )}

                                    {notif.actionable && !notif.responded && (
                                      <div className="mt-1.5 flex gap-1.5">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); respondToNotification(notif.notification_id, 'accept'); }}
                                          className="flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                                          data-testid={`notif-accept-${notif.notification_id}`}
                                        >
                                          <CheckCircle size={10} /> Accept
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); respondToNotification(notif.notification_id, 'decline'); }}
                                          className="flex items-center gap-1 px-2.5 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                                          data-testid={`notif-decline-${notif.notification_id}`}
                                        >
                                          <XCircle size={10} /> Decline
                                        </button>
                                      </div>
                                    )}

                                    {notif.responded && (
                                      <p className={`mt-1 text-[10px] font-medium ${notif.response_action === 'accept' ? 'text-green-600' : 'text-gray-500'}`}>
                                        {notif.response_action === 'accept' ? 'Accepted' : 'Declined'}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Shop — hidden below sm: to keep the right-rail compact at 375px.
              Still reachable via the sidebar and Cmd+K palette. */}
          {/* Persistent safety escalation — zero-friction "Need Help Now?" */}
          <button
            onClick={() => setShowSafetyNet(true)}
            className="help-shield-pulse flex items-center gap-1.5 pl-2 pr-2 sm:pr-3 py-1.5 rounded-full border transition-colors hover:bg-red-100"
            style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}
            aria-label="Get help now"
            data-testid="safety-net-button"
            title="Get help now"
          >
            <Shield size={20} fill="#DC2626" style={{ color: '#DC2626' }} />
            <span className="hidden sm:inline text-xs font-extrabold tracking-wide" style={{ color: '#B91C1C' }}>HELP</span>
          </button>

          <a
            href="/shop"
            className="nav-icon-button hidden sm:inline-flex"
            aria-label="Shop"
            data-testid="shop-button"
            title="HiBFF Shop"
          >
            <ShoppingBag size={20} className="text-orange-500" />
          </a>

          {/* Hearts / Pro Credits Balance — Phase C label + icon swap */}
          <div
            className="hearts-balance cursor-pointer"
            onClick={() => navigate(isAdultMode ? '/hearts?view=pro-credits' : '/hearts')}
            data-testid="header-credits-balance"
            title={isAdultMode ? 'Pro Credits' : 'Hearts'}
          >
            {isAdultMode ? (
              <Award size={18} className="text-amber-500" />
            ) : (
              <Heart size={18} className="text-red-500" fill="currentColor" />
            )}
            <span className="font-semibold">{user?.hearts_balance?.toLocaleString() || 0}</span>
          </div>

          {/* Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="profile-button"
              aria-label="Profile menu"
              data-testid="profile-button"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-orange-500 flex items-center justify-center text-white text-sm font-semibold">
                {user?.name?.charAt(0) || 'U'}
              </div>
            </button>

            {/* Profile Dropdown */}
            {showProfile && (
              <div className="nav-dropdown right-0">
                <div className="p-3 border-b border-gray-200">
                  <p className="font-semibold text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-600">{user?.email}</p>
                  <p className="text-xs text-orange-600 mt-1">{user?.subscription_tier_name}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { navigate('/settings'); setShowProfile(false); haptic.light(); }}
                    className="nav-dropdown-item w-full"
                  >
                    <Settings size={16} />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={() => {
                      nativeShare({
                        title: 'HiBFF — Practice Social Skills',
                        text: 'Practice real conversations with AI that feels real. Join me on HiBFF!',
                        url: window.location.origin,
                      });
                      haptic.medium();
                      setShowProfile(false);
                    }}
                    className="nav-dropdown-item w-full"
                    data-testid="share-app-button"
                  >
                    <Share2 size={16} />
                    <span>Share HiBFF</span>
                  </button>
                  <button onClick={() => { lockSession?.(); haptic.medium(); }} className="nav-dropdown-item w-full text-left" data-testid="lock-session-btn">
                    <Lock size={16} />
                    <span>Lock now (re-login required)</span>
                  </button>
                  <button onClick={() => { logout(); haptic.medium(); }} className="nav-dropdown-item w-full text-left" data-testid="logout-btn">
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
      {showSafetyNet && <SafetyNetModal onClose={() => setShowSafetyNet(false)} />}
    </>
  );
};

export default TopNav;
